import type { MouseEventHandler } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import SolidTimesCircleIcon from 'LuxKit/Icons/solid/SolidTimesCircleIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Clickable from 'components/Common/Clickable'
import { mediaQueryUp } from 'components/utils/breakpoint'
import focusStyle from 'styles/utils/focusStyle'

const SelectedChip = styled.div`
  display: none;
  grid-template-columns: minmax(0, 1fr) auto;
  min-width: 0;
  max-width: 100%;
  justify-content: space-between;
  height: auto;
  min-height: ${rem(28)};
  gap: ${rem(4)};
  padding: ${rem(4)} ${rem(16)};
  text-align: start;
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};
  box-shadow: inset 0 0 0 1px ${props => props.theme.palette.neutral.surface.lightForeground};
  ${mediaQueryUp.desktop} { display: inline-grid; }
`

const SelectionButton = styled(Clickable)`
  min-width: 0;
  text-align: start;
  &:focus-visible { ${focusStyle} }
`

const RemoveButton = styled(Clickable)`
  display: flex;
  align-items: center;
  &:focus-visible { ${focusStyle} }
`

const Label = styled(BodyText)`
  display: block;
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  line-height: ${rem(20)};
`

interface Props {
  count: number
  packageName: string
  onSelect: MouseEventHandler<HTMLButtonElement>
  onRemove: MouseEventHandler<HTMLButtonElement>
}

/** Navigate to a selected package independently from removing the whole choice. */
export default function AccommodationPackageSelectionPill({ count, packageName, onSelect, onRemove }: Props) {
  const intl = useIntl()
  const label = count > 1 ? intl.formatMessage({
    id: 'nrsR1UCRYQKc7NmktBKU',
    defaultMessage: '{count} × {packageName}',
    description: 'Selected package summary pill showing how many rooms share the same hotel package',
  }, { count, packageName }) : packageName
  const removeLabel = intl.formatMessage({
    id: 'Qc_KVLxEXG0YPylM2oJb',
    defaultMessage: 'Remove {count} × {packageName}',
    description: 'Accessible action that clears every selected room sharing this hotel package',
  }, { count, packageName })

  return <SelectedChip>
    <SelectionButton title={label} onClick={onSelect}>
      <Label variant="small">{label}</Label>
    </SelectionButton>
    <RemoveButton aria-label={removeLabel} onClick={onRemove}>
      <SolidTimesCircleIcon colour="normal-contrast" size="XS" />
    </RemoveButton>
  </SelectedChip>
}
